import React from 'react';
import { Dropdown, Button, Form, Badge } from 'react-bootstrap';

const TableHeader = ({
                         columns = [],
                         filters = {},
                         onChange = () => {},
                         onSort,
                         sortBy,
                         sortDir,
                         disabled = false,
                     }) => {
    const setFilter = (key, value) => {
        const nextFilters = {
            ...filters,
            [key]: value,
        };

        if (
            value === undefined ||
            value === '' ||
            value === null ||
            (Array.isArray(value) && value.length === 0)
        ) {
            delete nextFilters[key];
        }

        onChange(nextFilters);
    };

    const getFilterCount = (value) => {
        if (Array.isArray(value)) {
            return value.length;
        }

        if (value && typeof value === 'object') {
            return Object.values(value).filter(Boolean).length;
        }

        return value ? 1 : 0;
    };

    const SortIcon = ({ active }) => {
        return (
            <span className="ms-1 text-muted" aria-hidden>
                {active ? (sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕') : '↕'}
            </span>
        );
    };

    return (
        <thead>
        <tr>
            {columns.map((column) => {
                const filterValue = filters[column.key];
                const activeFilterCount = getFilterCount(filterValue);

                return (
                    <th key={column.key} className="align-middle">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                                <span
                                    role={onSort && column.sortable !== false ? 'button' : undefined}
                                    className={`user-select-none ${onSort && column.sortable !== false ? 'text-decoration-none' : ''}`}
                                    onClick={
                                        onSort && column.sortable !== false && !disabled
                                            ? () => onSort(column.key)
                                            : undefined
                                    }
                                >
                                    {column.label}
                                    {onSort && column.sortable !== false && (
                                        <SortIcon active={sortBy === column.key} />
                                    )}
                                </span>

                            {column.filter && (
                                <Dropdown align="end" autoClose="outside">
                                    <Dropdown.Toggle
                                        as={CustomToggle}
                                        id={`filter-${column.key}`}
                                        disabled={disabled}
                                        active={activeFilterCount > 0}
                                        count={activeFilterCount}
                                    />

                                    <Dropdown.Menu className="p-3" style={{ minWidth: 280 }}>
                                        {renderFilterContent(
                                            column,
                                            filterValue,
                                            (value) => setFilter(column.key, value)
                                        )}

                                        <div className="d-flex justify-content-end gap-2 mt-3">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                disabled={disabled}
                                                onClick={() => setFilter(column.key, undefined)}
                                            >
                                                Wissen
                                            </Button>

                                            <Button
                                                variant="primary"
                                                size="sm"
                                                disabled={disabled}
                                                onClick={(event) => {
                                                    const menu = event.currentTarget.closest('.dropdown-menu');

                                                    if (menu) {
                                                        menu.previousSibling?.click();
                                                    }
                                                }}
                                            >
                                                Sluiten
                                            </Button>
                                        </div>
                                    </Dropdown.Menu>
                                </Dropdown>
                            )}
                        </div>
                    </th>
                );
            })}
        </tr>
        </thead>
    );
};

const CustomToggle = React.forwardRef(
    ({ onClick, disabled, active, count }, ref) => (
        <Button
            ref={ref}
            variant={active ? 'primary' : 'outline-secondary'}
            size="sm"
            disabled={disabled}
            className="d-inline-flex align-items-center gap-1"
            onClick={(event) => {
                event.preventDefault();
                onClick?.(event);
            }}
            aria-label="Open kolomfilter"
        >
            <span aria-hidden>⏷</span>

            {count > 0 && (
                <Badge bg={active ? 'light' : 'secondary'} text={active ? 'dark' : undefined} pill>
                    {count}
                </Badge>
            )}
        </Button>
    )
);

CustomToggle.displayName = 'CustomToggle';

function renderFilterContent(column, value, setValue) {
    const type = column.filter?.type;
    const options = column.filter?.options || [];

    switch (type) {
        case 'text':
            return (
                <Form.Group>
                    <Form.Label className="mb-1">Bevat</Form.Label>
                    <Form.Control
                        autoFocus
                        placeholder={`Filter ${column.label.toLowerCase()}...`}
                        value={value ?? ''}
                        onChange={(event) => setValue(event.target.value)}
                    />
                </Form.Group>
            );

        case 'select':
            return (
                <Form.Group>
                    <Form.Label className="mb-1">Selecteer</Form.Label>
                    <Form.Select
                        value={value ?? ''}
                        onChange={(event) => setValue(event.target.value || undefined)}
                    >
                        <option value="">Alles</option>

                        {options.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            );

        case 'multiSelect':
            return (
                <Form.Group>
                    <Form.Label className="mb-2">Selecteer een of meer</Form.Label>

                    {options.length === 0 && (
                        <div className="text-muted small">
                            Geen opties beschikbaar.
                        </div>
                    )}

                    <div
                        className="d-flex flex-column gap-2"
                        style={{
                            maxHeight: 260,
                            overflowY: 'auto',
                        }}
                    >
                        {options.map((option) => {
                            const selected = Array.isArray(value) && value.includes(option);

                            return (
                                <Form.Check
                                    key={option}
                                    type="checkbox"
                                    id={`${column.key}-${option}`}
                                    label={option}
                                    checked={selected}
                                    onChange={() => {
                                        let next = Array.isArray(value) ? [...value] : [];

                                        if (selected) {
                                            next = next.filter((current) => current !== option);
                                        } else {
                                            next.push(option);
                                        }

                                        setValue(next.length ? next : undefined);
                                    }}
                                />
                            );
                        })}
                    </div>
                </Form.Group>
            );

        case 'numberRange':
            return (
                <>
                    <Form.Label className="mb-1">Bereik</Form.Label>

                    <div className="d-flex gap-2">
                        <Form.Control
                            type="number"
                            placeholder="Min"
                            value={value?.min ?? ''}
                            onChange={(event) =>
                                setValue({
                                    ...(value || {}),
                                    min: event.target.value || undefined,
                                })
                            }
                        />

                        <Form.Control
                            type="number"
                            placeholder="Max"
                            value={value?.max ?? ''}
                            onChange={(event) =>
                                setValue({
                                    ...(value || {}),
                                    max: event.target.value || undefined,
                                })
                            }
                        />
                    </div>
                </>
            );

        case 'dateRange':
            return (
                <>
                    <Form.Label className="mb-1">Datumbereik</Form.Label>

                    <div className="d-flex gap-2">
                        <Form.Control
                            type="date"
                            value={value?.from ?? ''}
                            onChange={(event) =>
                                setValue({
                                    ...(value || {}),
                                    from: event.target.value || undefined,
                                })
                            }
                        />

                        <Form.Control
                            type="date"
                            value={value?.to ?? ''}
                            onChange={(event) =>
                                setValue({
                                    ...(value || {}),
                                    to: event.target.value || undefined,
                                })
                            }
                        />
                    </div>
                </>
            );

        default:
            return (
                <div className="text-muted">
                    Er is geen filter voor deze kolom.
                </div>
            );
    }
}

export default TableHeader;
